import { useXhr } from '@fullstory/react-hooks';
import { getCacheKey } from '@fullstory/requests';
import { useMemo } from 'react';
import { QueryKey, queryOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { RequestLifecycleAction, RequestLifecycleActionReturn } from '../types';
import { useFetch } from './useXhrRequest';
import { toForcedRequestLifecycleAction } from '../utils';
import { getCommonRequest } from '../request';
import { NetworkController } from '@fullstory/util/xhr';
import { cacheKeyToQueryKey } from '@fullstory/requests/utils';

/**
 * Partially typed {@link UseQueryOptions} that work with fullstory's request framework.
 */
export type ReduxActionQueryOptions<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = ReadonlyArray<string>,
> = UseQueryOptions<TQueryFnData, unknown, TData, TQueryKey>;

/**
 * The subset of {@link ReduxActionQueryOptions} that can be customized.
 */
export type ConfigurableReduxActionQueryOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  Partial<ReduxActionQueryOptions<TQueryFnData, TData>>,
  'queryFn'
>;

/**
 * Takes an {@link RequestLifecycleAction} and returns options that can be used with react-query's
 * {@link useQuery} hook. The queryKey will by default be generated using our internal cache key logic,
 * and the queryFn will process the request through redux middleware.
 *
 * NB: the fetch function that's used for the queryFn updates the same internal cache as redux.
 * This means that we don't have to bother updating the internal cache when our
 * React Query cache changes. Our fetch function will always cause the internal
 * cache to update because it is processed using the same underlying function.
 * Similarly, any redux middleware includes callback options, `onCacheEntryDeleted`
 * and `onCacheEntryUpdated`, that write back to the React Query cache.
 *
 * @param action The {@link RequestLifecycleAction} created from a redux action creator.
 * @param xhr The {@link NetworkController} to use for the request.
 * @param fetch The legacy fetch function to use for sending the request.
 * @param opts Additional {@link UseQueryOptions} to pass to react-query's useQuery, sans queryFn.
 * A custom queryKey can optionally be provided, but this will break backwards compatibility with existing cache keys.
 *
 * @generic TBody the type of the request body
 * @generic TResponse the type of the response body
 * @generic TModify the type of the data after being modified by the action
 * @generic TData the type of the data modified by {@link UseQueryOptions.select}
 *
 * @returns Configured {@link UseQueryOptions} to use with react-query's {@link useQuery} hook.
 */
export const getReduxActionQueryOptions = <TBody, TResponse, TModify = TResponse, TData = TModify>(
  action: RequestLifecycleAction<any, any, any, TBody, TResponse, TModify>,
  xhr: NetworkController,
  fetch: <TAction extends RequestLifecycleAction<any, any, any, TBody, TResponse, TModify>>(
    action: TAction,
  ) => Promise<RequestLifecycleActionReturn<TAction>>,
  opts: ConfigurableReduxActionQueryOptions<TModify, TData> = {},
): ReduxActionQueryOptions<TModify, TData> => {
  // fetch it by dispatching a forced action through redux to allow react-query to drive the caching behavior.
  const forcedAction = toForcedRequestLifecycleAction(action);

  return queryOptions({
    ...opts,
    queryKey: opts.queryKey || cacheKeyToQueryKey(getCacheKey(getCommonRequest(forcedAction, xhr))),
    queryFn: () => fetch(forcedAction),
  });
};

/**
 * Takes an {@link RequestLifecycleAction} and returns options that can be used with react-query's
 * {@link useQuery} hook. The queryKey will by default be generated using our internal cache key logic,
 * and the queryFn will process the request through redux middleware.
 *
 * NB: the fetch function that's used for the queryFn updates the same internal cache as redux.
 * This means that we don't have to bother updating the internal cache when our
 * React Query cache changes. Our fetch function will always cause the internal
 * cache to update because it is processed using the same underlying function.
 * Similarly, any redux middleware includes callback options, `onCacheEntryDeleted`
 * and `onCacheEntryUpdated`, that write back to the React Query cache.
 *
 * @param action The {@link RequestLifecycleAction} created from a redux action creator.
 * @param opts Additional {@link UseQueryOptions} to pass to react-query's useQuery, sans queryFn.
 *
 * @generic TBody the type of the request body
 * @generic TResponse the type of the response body
 * @generic TModify the type of the data after being modified by the action
 * @generic TData the type of the data modified by {@link UseQueryOptions.select}
 *
 * A custom queryKey can optionally be provided, but this will break backwards compatibility with existing cache keys.
 * @returns Configured {@link UseQueryOptions} to use with react-query's {@link useQuery} hook.
 */
export const useReduxActionQueryOptions = <TBody, TResponse, TModify = TResponse, TData = TModify>(
  action: RequestLifecycleAction<any, any, any, TBody, TResponse, TModify>,
  opts: ConfigurableReduxActionQueryOptions<TModify, TData> = {},
): ReduxActionQueryOptions<TModify, TData> => {
  const xhr = useXhr();
  const fetch = useFetch();
  return useMemo(
    () => getReduxActionQueryOptions(action, xhr, fetch, opts),
    [action, xhr, fetch, opts],
  );
};
